import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { MediaQueryService } from '@supply-mover-2-0/core';
import {
	MEDIA_MAX_UPLOAD_BYTES,
	type MediaAssetDto,
	type MediaListQueryDto,
	type MediaListSortModel,
	type MediaMonthDto,
	type MediaType,
	type MediaUsedFilter,
} from '@supply-mover-2-0/shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { LoadingSpinner } from '../display/LoadingSpinner';
import { TablePaginationControls } from '../display/TablePaginationControls';
import { Callout } from '../feedback/Callout';
import { FileDropZone } from '../forms/FileDropZone';
import { Input } from '../forms/Input';
import { Select } from '../forms/Select';
import {
	acceptForMediaTypes,
	defaultUploadBrand,
	describeMediaBrand,
	describeMediaDimensions,
	describeMediaMonth,
	flattenMediaFolders,
	MEDIA_TYPE_LABELS,
	MEDIA_USED_FILTER_OPTIONS,
} from './mediaFormat';
import { MediaThumbnail } from './MediaThumbnail';
import { MediaUploadList } from './MediaUploadList';
import type { MediaLibraryDataModel } from './useMediaLibraryData';
import { useMediaUpload } from './useMediaUpload';

const ALL_TYPES: ReadonlyArray<MediaType> = ['image', 'svg', 'document'];

export interface MediaBrowserProps {
	/** Access, folders and tags, from `useMediaLibraryData`. */
	library: MediaLibraryDataModel;
	/** Show deleted items (the library's Deleted view) instead of live ones. */
	deleted?: boolean;
	/** The kinds of item on offer; a picker for a logo slot might offer only `svg` and `image`. */
	allowedTypes?: ReadonlyArray<MediaType>;
	/** Whether the upload area is shown (the caller checks media:upload). */
	canUpload: boolean;
	/** The item shown as selected. */
	selectedId?: number | null;
	onSelect: (asset: MediaAssetDto) => void;
	/** Double-click or Enter on a tile; a picker uses it to choose at once. */
	onActivate?: (asset: MediaAssetDto) => void;
	/** Brand to browse (and upload into) first. */
	initialBrand?: number | 'shared';
	/** Items per page. */
	pageSize?: number;
	/** Change it to reload the list, e.g. after the detail panel saved or deleted. */
	reloadKey?: number;
	/** Called after uploads finished, with the ones that became ready. */
	onUploaded?: (assets: Array<MediaAssetDto>) => void;
	/** Offer only items that can be used right now (a picker), not ones still being checked. */
	readyOnly?: boolean;
	/** Limits the grid's height so it scrolls inside a modal. */
	gridClassName?: string;
}

/**
 * PD-16339 — browse the media library: search, filter by brand, kind, folder, tag, where
 * the item is used and the month it was added, page through a grid of thumbnails, and add
 * files by dropping them or choosing them.
 * Used by the Media Library screen and by the picker, so what people see when choosing
 * a picture is the same library they manage.
 */
export function MediaBrowser({
	library,
	deleted = false,
	allowedTypes = ALL_TYPES,
	canUpload,
	selectedId,
	onSelect,
	onActivate,
	initialBrand,
	pageSize = 48,
	reloadKey = 0,
	onUploaded,
	readyOnly = false,
	gridClassName,
}: MediaBrowserProps): React.ReactElement {
	const { t } = useTranslation();
	const { access, folders, tags } = library;
	// Keyed by value, so a caller passing a new array each render does not reload the list.
	const typesKey = allowedTypes.join(',');
	const allowed = useMemo(
		() => typesKey.split(',').filter((type) => type !== '') as Array<MediaType>,
		[typesKey],
	);

	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [brand, setBrand] = useState<number | 'shared' | undefined>(
		initialBrand,
	);
	const [mediaType, setMediaType] = useState<MediaType | undefined>(undefined);
	const [folder, setFolder] = useState<number | 'none' | undefined>(undefined);
	const [tagId, setTagId] = useState<number | undefined>(undefined);
	const [used, setUsed] = useState<MediaUsedFilter | undefined>(undefined);
	const [month, setMonth] = useState<string | undefined>(undefined);
	const [months, setMonths] = useState<Array<MediaMonthDto>>([]);
	const [sort, setSort] = useState<MediaListSortModel>('newest');
	const [page, setPage] = useState(1);
	const [items, setItems] = useState<Array<MediaAssetDto> | null>(null);
	const [total, setTotal] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [localReload, setLocalReload] = useState(0);
	const [uploadBrand, setUploadBrand] = useState<number | null | undefined>(
		undefined,
	);

	const uploads = useMediaUpload({
		onSettled: () => {
			setLocalReload((value) => value + 1);
		},
	});

	useEffect(() => {
		const handle = setTimeout(() => {
			setDebouncedSearch(search.trim());
		}, 300);
		return (): void => {
			clearTimeout(handle);
		};
	}, [search]);

	// Any filter change starts again at the first page.
	useEffect(() => {
		setPage(1);
	}, [
		debouncedSearch,
		brand,
		mediaType,
		folder,
		tagId,
		used,
		month,
		sort,
		deleted,
	]);

	const query = useMemo((): MediaListQueryDto => {
		const typeFilter =
			mediaType ?? (allowed.length === 1 ? allowed[0] : undefined);
		return {
			search: debouncedSearch === '' ? undefined : debouncedSearch,
			brand,
			folder,
			tagId,
			used,
			month,
			mediaType: typeFilter,
			status: readyOnly ? 'ready' : undefined,
			deleted: deleted ? true : undefined,
			sort,
			page,
			pageSize,
		};
	}, [
		allowed,
		brand,
		debouncedSearch,
		deleted,
		folder,
		mediaType,
		page,
		pageSize,
		readyOnly,
		sort,
		tagId,
		used,
		month,
	]);

	// The month filter offers only months that have items, so it is refreshed whenever
	// the list is (an upload can start a new month; a delete can empty one).
	useEffect(() => {
		let isCurrent = true;
		void MediaQueryService.listMonths(deleted).then((result) => {
			if (isCurrent && result.success) {
				setMonths(result.data);
			}
		});
		return (): void => {
			isCurrent = false;
		};
	}, [deleted, reloadKey, localReload]);

	useEffect(() => {
		let isCurrent = true;
		setError(null);
		void MediaQueryService.list(query).then((result) => {
			if (!isCurrent) {
				return;
			}
			if (!result.success) {
				setError(result.error ?? 'The media library could not be loaded.');
				return;
			}
			// The server filters by one kind at a time; a picker for two kinds hides the third.
			setItems(
				result.data.items.filter((item) => allowed.includes(item.mediaType)),
			);
			setTotal(result.data.total);
		});
		return (): void => {
			isCurrent = false;
		};
	}, [allowed, query, reloadKey, localReload]);

	const handleFiles = useCallback(
		(files: Array<File>): void => {
			if (access == null) {
				return;
			}
			const brandId =
				uploadBrand !== undefined
					? uploadBrand
					: defaultUploadBrand(access, brand);
			void uploads
				.upload(files, {
					brandId,
					folderId: typeof folder === 'number' ? folder : null,
				})
				.then((done) => {
					const ready = done.filter((asset) => asset.status === 'ready');
					onUploaded?.(ready);
				});
		},
		[access, brand, folder, onUploaded, uploadBrand, uploads],
	);

	const folderOptions = useMemo(() => flattenMediaFolders(folders), [folders]);
	const showBrands =
		access != null && (access.brands.length > 1 || access.canManageShared);
	const totalPages = Math.max(1, Math.ceil(total / pageSize));

	const brandFilterOptions = [
		{ value: '', label: t('All brands') },
		{ value: 'shared', label: t('Shared') },
		...(access?.brands ?? []).map((option) => ({
			value: String(option.id),
			label: t(option.name),
		})),
	];

	const uploadBrandOptions = [
		...(access?.canManageShared === true
			? [{ value: 'shared', label: t('Shared (every brand)') }]
			: []),
		...(access?.brands ?? []).map((option) => ({
			value: String(option.id),
			label: t(option.name),
		})),
	];

	const effectiveUploadBrand =
		access == null
			? null
			: uploadBrand !== undefined
				? uploadBrand
				: defaultUploadBrand(access, brand);

	return (
		<div className="flex flex-col gap-3">
			{/* Phones: search on its own line, filters two to a row. Wider: one row where search grows
			    and the filters keep a fixed width, wrapping only when they must. */}
			<div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:[&>*]:w-40">
				<div className="col-span-2 sm:min-w-[16rem] sm:flex-1">
					<Input
						icon={faSearch}
						value={search}
						size="sm"
						fullWidth
						placeholder={t('Search title, file name, alt text')}
						aria-label={t('Search media')}
						onChange={(event) => {
							setSearch(event.target.value);
						}}
					/>
				</div>
				{showBrands && (
					<Select
						size="sm"
						aria-label={t('Brand')}
						value={brand == null ? '' : String(brand)}
						options={brandFilterOptions}
						onChange={(value) => {
							setBrand(
								value === ''
									? undefined
									: value === 'shared'
										? 'shared'
										: Number(value),
							);
						}}
					/>
				)}
				{allowed.length > 1 && (
					<Select
						size="sm"
						aria-label={t('Kind')}
						value={mediaType ?? ''}
						options={[
							{ value: '', label: t('All kinds') },
							...allowed.map((type) => ({
								value: type,
								label: t(MEDIA_TYPE_LABELS[type]),
							})),
						]}
						onChange={(value) => {
							setMediaType(value === '' ? undefined : (value as MediaType));
						}}
					/>
				)}
				<Select
					size="sm"
					aria-label={t('Folder')}
					value={folder == null ? '' : String(folder)}
					options={[
						{ value: '', label: t('All folders') },
						{ value: 'none', label: t('Not in a folder') },
						...folderOptions.map((option) => ({
							value: String(option.id),
							label: `${'   '.repeat(option.depth)}${t(option.label)}`,
						})),
					]}
					onChange={(value) => {
						setFolder(
							value === ''
								? undefined
								: value === 'none'
									? 'none'
									: Number(value),
						);
					}}
				/>
				{tags.length > 0 && (
					<Select
						size="sm"
						aria-label={t('Tag')}
						value={tagId == null ? '' : String(tagId)}
						options={[
							{ value: '', label: t('All tags') },
							...tags.map((tag) => ({
								value: String(tag.id),
								label: t(tag.name),
							})),
						]}
						onChange={(value) => {
							setTagId(value === '' ? undefined : Number(value));
						}}
					/>
				)}
				<Select
					size="sm"
					aria-label={t('Where used')}
					value={used ?? ''}
					options={[
						{ value: '', label: t('Used or unused') },
						...MEDIA_USED_FILTER_OPTIONS.map((option) => ({
							value: option.value,
							label: t(option.label),
						})),
					]}
					onChange={(value) => {
						setUsed(value === '' ? undefined : (value as MediaUsedFilter));
					}}
				/>
				{(months.length > 0 || month != null) && (
					<Select
						size="sm"
						aria-label={t('Month added')}
						value={month ?? ''}
						options={[
							{ value: '', label: t('All months') },
							...months.map((option) => ({
								value: option.month,
								label: t('{{month}} ({{count}})', {
									month: t(describeMediaMonth(option.month)),
									count: option.count,
								}),
							})),
						]}
						onChange={(value) => {
							setMonth(value === '' ? undefined : value);
						}}
					/>
				)}
				<Select
					size="sm"
					aria-label={t('Sort')}
					value={sort}
					options={[
						{
							value: 'newest',
							label: t(deleted ? 'Recently deleted' : 'Newest first'),
						},
						{ value: 'oldest', label: t('Oldest first') },
						{ value: 'title', label: t('Title A to Z') },
					]}
					onChange={(value) => {
						setSort(value as MediaListSortModel);
					}}
				/>
			</div>

			{canUpload && !deleted && access != null && (
				<div className="flex flex-col gap-2 md:flex-row md:items-stretch">
					<FileDropZone
						compact
						className="flex-1"
						accept={acceptForMediaTypes(allowed)}
						onFiles={handleFiles}
						label={t('Drop files here, or click to choose')}
						hint={t(
							'JPEG, PNG, WebP, AVIF, SVG or PDF, up to {{size}} MB each. Pictures are resized and made web-ready automatically.',
							{ size: MEDIA_MAX_UPLOAD_BYTES / (1024 * 1024) },
						)}
					/>
					{uploadBrandOptions.length > 1 && (
						<div className="md:w-56">
							<Select
								size="sm"
								aria-label={t('Upload to')}
								value={
									effectiveUploadBrand == null
										? 'shared'
										: String(effectiveUploadBrand)
								}
								options={uploadBrandOptions}
								onChange={(value) => {
									setUploadBrand(value === 'shared' ? null : Number(value));
								}}
							/>
							<p className="mt-1 text-xs text-muted">
								{t('New uploads go to this brand.')}
							</p>
						</div>
					)}
				</div>
			)}

			<MediaUploadList
				entries={uploads.entries}
				onDismiss={uploads.dismiss}
				onClearFinished={uploads.clearFinished}
			/>

			{error != null && <Callout variant="danger">{t(error)}</Callout>}

			{error == null && items == null && (
				<div className="flex justify-center py-10">
					<LoadingSpinner label={t('Loading media...')} showLabel />
				</div>
			)}

			{error == null && items != null && items.length === 0 && (
				<div className="rounded-lg border border-dashed border-default px-6 py-10 text-center">
					<p className="text-sm font-medium text-default">
						{t(deleted ? 'Nothing has been deleted' : 'No media found')}
					</p>
					<p className="mt-1 text-sm text-muted">
						{t(
							deleted
								? 'Deleted items stay here for 30 days before they are removed for good.'
								: debouncedSearch !== '' ||
									  brand != null ||
									  folder != null ||
									  tagId != null
									? 'Nothing matches these filters. Clear a filter or search for something else.'
									: 'Pictures, SVG logos and PDFs you upload appear here, ready to use on websites, emails and promotions.',
						)}
					</p>
				</div>
			)}

			{items != null && items.length > 0 && (
				<div
					className={cn(
						'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6',
						gridClassName,
					)}
				>
					{items.map((item) => {
						const isSelected = item.id === selectedId;
						const dimensions = describeMediaDimensions(item);
						return (
							<button
								key={item.id}
								type="button"
								aria-pressed={isSelected}
								className={cn(
									'group flex flex-col overflow-hidden rounded-lg border bg-surface text-left transition-colors focus:outline-none focus:ring-2 focus:ring-accent',
									isSelected
										? 'border-accent ring-2 ring-accent'
										: 'border-muted hover:border-accent',
								)}
								onClick={() => {
									onSelect(item);
								}}
								onDoubleClick={() => {
									onActivate?.(item);
								}}
								onKeyDown={(event) => {
									if (event.key === 'Enter' && onActivate != null) {
										event.preventDefault();
										onActivate(item);
									}
								}}
							>
								<MediaThumbnail
									asset={item}
									// A logo is shown whole; a photo fills the tile around its focal point.
									fit={item.mediaType === 'svg' ? 'contain' : 'cover'}
									className={cn(
										'aspect-square w-full',
										item.mediaType === 'svg' && 'p-3',
									)}
								/>
								<div className="min-w-0 px-2 py-1.5">
									<p className="truncate text-sm text-default">
										{t(item.title)}
									</p>
									<p className="truncate text-xs text-muted">
										{t(MEDIA_TYPE_LABELS[item.mediaType])}
										{dimensions != null && ` · ${t(dimensions)}`}
										{brand == null &&
											showBrands &&
											` · ${t(describeMediaBrand(item.brandId, access))}`}
									</p>
								</div>
							</button>
						);
					})}
				</div>
			)}

			{totalPages > 1 && (
				<TablePaginationControls
					currentPage={page}
					totalPages={totalPages}
					onPageChange={setPage}
					startIndex={(page - 1) * pageSize + 1}
					endIndex={Math.min(page * pageSize, total)}
					totalItems={total}
				/>
			)}
		</div>
	);
}
